import { useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { LiveActionType, LiveSessionStateI } from "@interfaces";
import { ValidateCurrentToken, getSocketBaseUrl } from "../../helpers";

interface UseLiveEventSocketProps {
  eventId: string | undefined;
}

// el JWT ya trae { data: { userId } } sin encriptar — lo decodificamos acá
// nomás para saber "soy yo quien inició esta corrida", sin pegarle a la API.
const decodeUserId = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.data?.userId ?? null;
  } catch {
    return null;
  }
};

export const useLiveEventSocket = ({ eventId }: UseLiveEventSocketProps) => {
  const socketRef = useRef<Socket | null>(null);
  const [state, setState] = useState<LiveSessionStateI | null>(null);
  const [presence, setPresence] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);

  const currentUserId = useMemo(() => {
    const token = ValidateCurrentToken() as unknown as string;
    return token ? decodeUserId(token) : null;
  }, []);

  useEffect(() => {
    if (!eventId) return;

    const token = ValidateCurrentToken() as unknown as string;
    if (!token) return;

    const socket = io(getSocketBaseUrl(), {
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("live:join", { eventId, token });
    });

    socket.on("disconnect", () => setConnected(false));

    socket.on("live:state", (nextState: LiveSessionStateI) => {
      setState(nextState);
    });

    socket.on("live:presence", ({ names }: { names: string[] }) => {
      setPresence(names);
    });

    return () => {
      socket.emit("live:leave", { eventId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [eventId]);

  const dispatch = (type: LiveActionType, payload?: { playId?: string }) => {
    if (!eventId) return;
    const token = ValidateCurrentToken() as unknown as string;
    socketRef.current?.emit("live:action", { eventId, token, type, payload });
  };

  return {
    state,
    presence,
    connected,
    currentUserId,
    start: () => dispatch("start"),
    next: () => dispatch("next"),
    prev: () => dispatch("prev"),
    check: () => dispatch("check"),
    activatePlay: (playId: string) => dispatch("playActivate", { playId }),
    confirmPlay: () => dispatch("playConfirm"),
    cancelPlay: () => dispatch("playCancel"),
    finish: () => dispatch("finish"),
    clearLog: () => dispatch("clearLog"),
  };
};
